import React, { useRef, useEffect } from "react";
import { FormInput } from "@/components/basic/FormInput";
import { RegisterUserPasswordField } from "@/components/RegisterUserPasswordField/RegisterUserPasswordField";
import { FormSelect } from "@/components/basic/FormSelect";
import { useAppSelector } from "@/app/hooks";
import {
  selectIsAuthenticate,
} from "@/core/store/authSlice";

type Props = {
  values: {
    email: string;
    title: string;
    telephone1: string;
    forename: string;
    surname: string;
    [key: string]: string | boolean | undefined;
  };
  touched: {
    [key: string]: boolean | undefined;
  };
  errors: {
    [key: string]: string | undefined;
  };
  handleChange: (e: React.FormEvent<any>) => void;
  setFieldValue: Function;
  handleBlur: (e: React.FocusEvent<any, Element>) => void;
};

const isValidField = (
  values: Props["values"],
  touched: Props["touched"],
  errors: Props["errors"],
  field: string
): boolean => {
  return !!touched[field] && !errors[field] && !!values[field];
};

export const PurchaserDetails = React.memo(
  ({
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
  }: Props) => {
    const lastEmail = useRef(values.email);
    const setFieldValueRef = useRef(setFieldValue);
    useEffect(() => { setFieldValueRef.current = setFieldValue; }, [setFieldValue]);

    // ── Email identity bridge ─────────────────────────────────────────────────
    // Fires once per session when a valid email is entered at checkout.
    // Links this session_id → email in beast's event_log so cross-session
    // attribution queries can stitch journeys where the ad click and checkout
    // happened in different browser sessions (e.g. ad on phone, buy on desktop).
    const identityFiredRef = useRef(false);
    useEffect(() => {
      if (identityFiredRef.current) return;
      if (!values.email || !values.email.includes('@') || !values.email.includes('.')) return;
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('_nr_identity_fired')) return;
      identityFiredRef.current = true;
      try { sessionStorage.setItem('_nr_identity_fired', '1'); } catch (_) {}
      const sessionId = (typeof localStorage !== 'undefined' && localStorage.getItem('nr_session_id')) || '';
      const pathMatch = window.location.pathname.match(/\/number-plates\/(?:reg\/)?([A-Za-z0-9][A-Za-z0-9\-]{0,19})/);
      const registration = pathMatch ? pathMatch[1].replace(/[-\s+]+/g, '').toUpperCase() : '';
      fetch('/api/track/identity', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ session_id: sessionId, email: values.email, registration: registration || undefined }),
        keepalive: true,
      }).catch(() => {});
    }, [values.email]);

    // Poll DOM value every 200ms — catches iOS autofill regardless of which events fire.
    // Only syncs Formik state; no account-existence check (prevents enumeration).
    useEffect(() => {
      const id = setInterval(() => {
        const input = document.getElementById('email') as HTMLInputElement | null;
        if (!input) return;
        const val = input.value;
        if (!val) { lastEmail.current = ""; return; }
        if (val !== lastEmail.current) {
          lastEmail.current = val;
          setFieldValueRef.current('email', val); // sync Formik — stop DOM reset on iOS
        }
      }, 200);
      return () => clearInterval(id);
    }, []);
    const isAuthenticate = useAppSelector(selectIsAuthenticate);

    return (
      <>
        <FormInput
          label="Email"
          placeholder="Your email address"
          type="email"
          autocomplete="email"
          disabled={isAuthenticate}
          onChange={handleChange}
          onBlur={handleBlur}
          name="email"
          autoCapitalize="none"
          autoCorrect="off"
          value={values.email}
          errorMsg={touched.email ? errors.email : ""}
          valid={isValidField(values, touched, errors, "email")}
        />
        <RegisterUserPasswordField
          email={errors.email ? "" : values.email}
        ></RegisterUserPasswordField>
        <FormInput
          label="Phone number"
          placeholder="Your phone number"
          type="tel"
          autocomplete="tel"
          name="telephone1"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.telephone1}
          errorMsg={touched.telephone1 ? errors.telephone1 : ""}
          valid={isValidField(values, touched, errors, "telephone1")}
        />
        <div className="grid grid-cols-5 gap-4">
          <FormSelect
            label="Title"
            options={["Mr", "Miss", "Mrs", "Ms"]}
            onChange={(e) => {
              const target = e.target as typeof e.target & { value: string };
              setFieldValue("title", target.value);
            }}
            onBlur={handleBlur}
            wrapperClassName="lg:col-span-1 col-span-full"
            name="title"
            autocomplete="honorific-prefix"
            value={values.title}
            errorMsg={touched.title ? errors.title : ""}
            valid={isValidField(values, touched, errors, "title")}
          />
          <FormInput
            name="forename"
            autocomplete="given-name"
            wrapperClassName="lg:col-span-2 col-span-full"
            label="First name"
            placeholder="First name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.forename}
            errorMsg={touched.forename ? errors.forename : ""}
            valid={isValidField(values, touched, errors, "forename")}
          />
          <FormInput
            name="surname"
            autocomplete="family-name"
            wrapperClassName="lg:col-span-2 col-span-full"
            label="Surname"
            placeholder="Surname"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.surname}
            errorMsg={touched.surname ? errors.surname : ""}
            valid={isValidField(values, touched, errors, "surname")}
          />
        </div>
      </>
    );
  }
);
